import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { getBlogPost, formatBlogDate } from "@/data/blogPosts";
import { COURSE_LIST } from "@/data/courses";
import { ReadAloudButton } from "@/components/site/ReadAloudButton";

const JSONLD_ID = "blog-article-jsonld";

function injectArticleJsonLd(post, url) {
  document.getElementById(JSONLD_ID)?.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = JSONLD_ID;
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.datePublished,
    dateModified: post.dateModified,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    author: { "@type": "Organization", name: "Dickies-Campus GmbH" },
    publisher: {
      "@type": "Organization",
      name: "Dickies-Campus GmbH",
      logo: { "@type": "ImageObject", url: "https://dickies-helden.de/dh_logo_terra.png" },
    },
  });
  document.head.appendChild(script);
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPost(slug);
  const course = post ? COURSE_LIST.find((c) => c.id === post.relatedCourseSlug) : null;

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Dickies-Campus`;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = post.metaDescription;
    injectArticleJsonLd(post, window.location.href);
    window.scrollTo(0, 0);
    return () => document.getElementById(JSONLD_ID)?.remove();
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-24" data-testid="blog-article-page">
        <Link to="/blog" data-testid="blog-back-link" className="inline-flex items-center gap-1.5 text-sm text-sand-500 hover:text-sand-800">
          <ArrowLeft className="w-4 h-4" /> Alle Artikel
        </Link>

        <article>
          <div className="mt-6 text-xs uppercase tracking-widest text-terra font-bold">
            {formatBlogDate(post.datePublished)}
          </div>
          <h1 className="mt-3 font-heading text-4xl md:text-5xl font-semibold text-sand-800 tracking-tight normal-case">
            {post.title}
          </h1>
          <div className="mt-5">
            <ReadAloudButton
              label="Artikel vorlesen"
              text={[post.title, ...post.body.map((b) => (Array.isArray(b.items) ? b.items.join(". ") : b.text || ""))].filter(Boolean).join(" ")}
            />
          </div>

          <div className="mt-8 space-y-5 text-sand-700 text-[15px] leading-relaxed">
            {post.body.map((block, i) => {
              if (block.type === "h2") {
                return (
                  <h2 key={i} className="font-heading text-xl md:text-2xl text-sand-800 font-semibold normal-case pt-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === "ul") {
                return (
                  <ul key={i} className="list-disc pl-5 space-y-2">
                    {block.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                );
              }
              if (block.linkText && block.linkTo && block.text.includes(block.linkText)) {
                const [before, after] = block.text.split(block.linkText);
                return (
                  <p key={i}>
                    {before}
                    <Link to={block.linkTo} className="underline text-terra font-semibold">{block.linkText}</Link>
                    {after}
                  </p>
                );
              }
              return <p key={i}>{block.text}</p>;
            })}
          </div>
        </article>

        {post.relatedBox && (
          <aside data-testid="blog-related-box" className="mt-12 rounded-2xl border border-terra/30 bg-white/70 p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-terra font-bold">{post.relatedBox.kicker}</div>
            <div className="mt-2 font-heading text-xl md:text-2xl font-semibold text-sand-800 normal-case">
              {post.relatedBox.title}
            </div>
            <p className="mt-1 text-sand-700 text-sm">{post.relatedBox.subtitle}</p>
            <div className="mt-5">
              <Button asChild className="bg-terra hover:bg-terra/90 text-cream-50 rounded-full" data-testid="blog-related-box-cta">
                <Link to={post.relatedBox.to}>
                  {post.relatedBox.cta} <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>
          </aside>
        )}

        {course && (
          <aside
            data-testid="blog-related-course-box"
            className="mt-12 rounded-2xl border border-terra/30 bg-white/70 p-6 sm:p-8"
          >
            <div className="text-xs uppercase tracking-widest text-terra font-bold">Passende Weiterbildung</div>
            <div className="mt-2 font-heading text-xl md:text-2xl font-semibold text-sand-800 normal-case">
              {course.title}
            </div>
            <p className="mt-1 text-sand-700 text-sm">{course.subtitle}</p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-terra hover:bg-terra/90 text-cream-50 rounded-full" data-testid="blog-course-link">
                <Link to={`/kurse/${course.id}`}>
                  Zur Kursseite <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-sand-800/25 text-sand-800" data-testid="blog-beratung-cta">
                <Link to="/#kontakt">Kostenlose Beratung</Link>
              </Button>
            </div>
          </aside>
        )}
      </main>
      <Footer />
    </div>
  );
}
